import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tenantsService, TenantWithAdminCreate, TenantWithAdminResponse } from '@/services/tenants';
import { useAuthStore } from '@/context/authStore';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Modal from '@/components/ui/Modal';
import {
  Building2,
  Plus,
  Mail,
  User,
  Key,
  CheckCircle,
  XCircle,
  Copy,
  Phone,
  MapPin,
  Calendar,
  Stethoscope,
  Activity,
  Users,
} from 'lucide-react';

export default function Tenants() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [successData, setSuccessData] = useState<TenantWithAdminResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState<TenantWithAdminCreate>({
    tenant_name: '',
    tenant_slug: '',
    tenant_address: '',
    tenant_phone: '',
    tenant_email: '',
    admin_email: '',
    admin_full_name: '',
  });

  const { data: tenants, isLoading } = useQuery({
    queryKey: ['tenants'],
    queryFn: tenantsService.getTenants,
  });

  const createTenantMutation = useMutation({
    mutationFn: tenantsService.createTenant,
    onSuccess: (data) => {
      setSuccessData(data);
      setFormData({
        tenant_name: '',
        tenant_slug: '',
        tenant_address: '',
        tenant_phone: '',
        tenant_email: '',
        admin_email: '',
        admin_full_name: '',
      });
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
  });

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setSuccessData(null);
    setCopied(false);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSuccessData(null);
    setCopied(false);
    createTenantMutation.reset();
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setFormData({
      ...formData,
      tenant_name: name,
      tenant_slug: generateSlug(name),
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanedData: TenantWithAdminCreate = {
      tenant_name: formData.tenant_name,
      tenant_slug: formData.tenant_slug,
      admin_email: formData.admin_email,
      admin_full_name: formData.admin_full_name,
      ...(formData.tenant_address && { tenant_address: formData.tenant_address }),
      ...(formData.tenant_phone && { tenant_phone: formData.tenant_phone }),
      ...(formData.tenant_email && { tenant_email: formData.tenant_email }),
    };
    createTenantMutation.mutate(cleanedData);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Buenos días';
    if (hour < 18) return 'Buenas tardes';
    return 'Buenas noches';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const activeTenants = tenants?.filter((t) => t.is_active) || [];

  return (
    <div className="space-y-8">
      {/* Header con saludo */}
      <div className="relative overflow-hidden bg-gradient-to-br from-taupe-600 via-taupe-700 to-taupe-800 rounded-2xl p-8 text-white">
        {/* Decorative shapes */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-cream-400/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-cream-300/10 rounded-full blur-2xl translate-y-1/2 -translate-x-1/4"></div>
        </div>

        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-cream-300 text-sm">Panel de Administración</span>
            </div>
            <h1 className="text-3xl font-bold text-cream-50">
              {getGreeting()}, {user?.full_name?.split(' ')[0]}
            </h1>
            <p className="text-taupe-200 mt-1">
              Gestiona las veterinarias del sistema
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={handleOpenModal}
              className="bg-cream-100 text-taupe-800 hover:bg-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nueva Veterinaria
            </Button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl p-6 border border-gray-100">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <Building2 className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-3xl font-bold text-gray-900">{activeTenants.length}</p>
              <p className="text-sm text-gray-500">Veterinarias Activas</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-gray-100">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
              <Users className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-3xl font-bold text-gray-900">{tenants?.length || 0}</p>
              <p className="text-sm text-gray-500">Total Clientes</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-gray-100">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20">
              <Activity className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-3xl font-bold text-gray-900">99.9%</p>
              <p className="text-sm text-gray-500">Uptime del Sistema</p>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de veterinarias */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-taupe-100 rounded-lg flex items-center justify-center">
              <Building2 className="h-4 w-4 text-taupe-600" />
            </div>
            <h2 className="font-semibold text-gray-900">Veterinarias</h2>
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-12 text-gray-500">Cargando...</div>
        ) : tenants && tenants.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {tenants.map((tenant) => (
              <div
                key={tenant.id}
                className="p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-taupe-500 to-taupe-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Stethoscope className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-semibold text-gray-900">{tenant.name}</h3>
                        <p className="text-sm text-gray-500">/{tenant.slug}</p>
                      </div>
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          tenant.is_active
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {tenant.is_active ? 'Activa' : 'Inactiva'}
                      </span>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-4 text-sm text-gray-600">
                      {tenant.phone && (
                        <div className="flex items-center gap-1.5">
                          <Phone className="h-4 w-4 text-gray-400" />
                          {tenant.phone}
                        </div>
                      )}
                      {tenant.email && (
                        <div className="flex items-center gap-1.5">
                          <Mail className="h-4 w-4 text-gray-400" />
                          {tenant.email}
                        </div>
                      )}
                      {tenant.address && (
                        <div className="flex items-center gap-1.5">
                          <MapPin className="h-4 w-4 text-gray-400" />
                          {tenant.address}
                        </div>
                      )}
                      <div className="flex items-center gap-1.5">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        Desde {formatDate(tenant.created_at)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building2 className="h-8 w-8 text-gray-400" />
            </div>
            <p className="text-gray-500 font-medium">No hay veterinarias registradas</p>
            <p className="text-sm text-gray-400 mt-1">
              Usa el botón "Nueva Veterinaria" para crear la primera
            </p>
            <Button onClick={handleOpenModal} className="mt-4">
              <Plus className="h-4 w-4 mr-2" />
              Crear Veterinaria
            </Button>
          </div>
        )}
      </div>

      {/* Modal de crear cliente */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={successData ? 'Veterinaria Creada' : 'Nueva Veterinaria'}
        size="lg"
      >
        {successData ? (
          <div className="space-y-6">
            <div className="flex items-center justify-center">
              <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
            </div>

            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900">
                {successData.tenant.name}
              </h3>
              <p className="text-gray-500">ha sido creada exitosamente</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 space-y-4">
              <h4 className="font-medium text-gray-900 flex items-center gap-2">
                <User className="h-4 w-4" />
                Datos del Administrador
              </h4>

              <div className="space-y-3">
                <div className="flex items-center justify-between bg-white p-3 rounded-lg border">
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="font-medium">{successData.admin_email}</p>
                  </div>
                  <Mail className="h-4 w-4 text-gray-400" />
                </div>

                <div className="flex items-center justify-between bg-white p-3 rounded-lg border">
                  <div>
                    <p className="text-xs text-gray-500">Contraseña Temporal</p>
                    <p className="font-mono text-lg font-bold text-taupe-700">
                      {successData.temporary_password}
                    </p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(successData.temporary_password)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Copiar contraseña"
                  >
                    {copied ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <Copy className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              <div
                className={`flex items-center gap-2 p-3 rounded-lg ${
                  successData.email_sent
                    ? 'bg-green-50 text-green-700'
                    : 'bg-amber-50 text-amber-700'
                }`}
              >
                {successData.email_sent ? (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm">Email de bienvenida enviado</span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4" />
                    <span className="text-sm">
                      Email no enviado - comparte la contraseña manualmente
                    </span>
                  </>
                )}
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <strong>Importante:</strong> El usuario deberá cambiar su contraseña en
                el primer inicio de sesión.
                {!successData.email_sent &&
                  ' Asegúrate de compartir las credenciales de forma segura.'}
              </p>
            </div>

            <div className="flex justify-end">
              <Button onClick={handleCloseModal}>Cerrar</Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {createTenantMutation.isError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {(createTenantMutation.error as any)?.response?.data?.detail ||
                  'Error al crear el cliente'}
              </div>
            )}

            <div className="space-y-4">
              <h4 className="font-medium text-gray-900 flex items-center gap-2 pb-2 border-b">
                <Building2 className="h-4 w-4" />
                Datos de la Veterinaria
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Nombre de la Veterinaria *"
                  value={formData.tenant_name}
                  onChange={handleNameChange}
                  placeholder="Ej: Veterinaria San Roque"
                  required
                />
                <Input
                  label="Identificador (slug) *"
                  value={formData.tenant_slug}
                  onChange={(e) => setFormData({ ...formData, tenant_slug: e.target.value })}
                  placeholder="ej: veterinaria-san-roque"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Teléfono"
                  value={formData.tenant_phone}
                  onChange={(e) => setFormData({ ...formData, tenant_phone: e.target.value })}
                  placeholder="Ej: +54 11 1234-5678"
                />
                <Input
                  label="Email de la Veterinaria"
                  type="email"
                  value={formData.tenant_email}
                  onChange={(e) => setFormData({ ...formData, tenant_email: e.target.value })}
                  placeholder="info@veterinaria.com"
                />
              </div>

              <Input
                label="Dirección"
                value={formData.tenant_address}
                onChange={(e) => setFormData({ ...formData, tenant_address: e.target.value })}
                placeholder="Ej: Av. Corrientes 1234, CABA"
              />
            </div>

            <div className="space-y-4">
              <h4 className="font-medium text-gray-900 flex items-center gap-2 pb-2 border-b">
                <User className="h-4 w-4" />
                Datos del Administrador
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Nombre Completo *"
                  value={formData.admin_full_name}
                  onChange={(e) =>
                    setFormData({ ...formData, admin_full_name: e.target.value })
                  }
                  placeholder="Ej: Juan Pérez"
                  required
                />
                <Input
                  label="Email del Administrador *"
                  type="email"
                  value={formData.admin_email}
                  onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                  placeholder="admin@veterinaria.com"
                  required
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800 flex items-start gap-2">
                  <Key className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  Se generará una contraseña temporal automáticamente y se enviará por
                  email al administrador.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="secondary" onClick={handleCloseModal}>
                Cancelar
              </Button>
              <Button type="submit" loading={createTenantMutation.isPending}>
                <Plus className="h-4 w-4 mr-2" />
                Crear Veterinaria
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
